import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import { Skeleton } from '@pagespeedify/ui';
import { ReportUsageResponse } from '../types/reports';

const UsageTabs: React.FC = () => {
  const apiClient = useApi();
  const { user, isLoading: authIsLoading } = useAuth();

  // Fetch reports usage data
  const {
    data: reportsUsage,
    isLoading: isLoadingReports,
    isError: isReportsError,
  } = useQuery<ReportUsageResponse, Error>({
      queryKey: ['reportUsage', user?.uid],
      queryFn: async () => {
          if (!apiClient) throw new Error('API client not available');
          const response = await apiClient.get<{ success: boolean, usage: ReportUsageResponse, message?: string }>('/reports/usage');
          if (!response.success || !response.usage) {
              throw new Error(response.message || 'Failed to fetch usage data.');
          }
          return response.usage;
      },
      enabled: !!user && !authIsLoading && !!apiClient,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
  });

  return (
    <div className="w-full">
      {isLoadingReports ? (
        <div className="p-2 text-center">
          <Skeleton className="h-4 w-full" />
        </div>
      ) : isReportsError ? (
        <div className="p-2 text-center">
          <p className="text-xs text-destructive">Usage data unavailable.</p>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground px-2 py-1">
          <span>Used: {reportsUsage?.consumedInPeriod}/{reportsUsage?.limitForPeriod}</span>
          <span className="ml-2">Remaining: {reportsUsage?.remainingInBundle}</span>
          {reportsUsage && reportsUsage.overageCostPerUnit !== null && reportsUsage.overageCostPerUnit > 0 && (
              <span className="ml-2 block">Overlimit: {reportsUsage.overageCostPerUnit} credits/report</span>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageTabs;
